import type { ConnectionDto, NodeDto, RoomSnapshotDto, StrokeDto, TransformDto } from '../generated/contracts';
import type { NodeLockDto, PresenceDto } from '../net/wire';

/**
 * What the view is told when truth changes. Kept separate from the scene so the
 * reconciliation rules can be reasoned about — and tested — without a renderer.
 */
export interface RoomStateListener {
  onReset(state: RoomState): void;
  onNodeUpserted(node: NodeDto): void;
  onNodeRemoved(nodeId: string): void;
  onConnectionUpserted(connection: ConnectionDto): void;
  onConnectionRemoved(connectionId: string): void;
  onStrokeUpserted(stroke: StrokeDto): void;
  onStrokeRemoved(strokeId: string): void;
  onLocksChanged(): void;
  onPeersChanged(): void;
}

/**
 * Mirror of server truth for one room.
 *
 * The server is authoritative. The only thing this holds optimistically is a node you
 * hold the lock on — and while you hold it nobody else can change it, so there is no
 * conflict to resolve by construction.
 */
export class RoomState {
  readonly nodes = new Map<string, NodeDto>();
  readonly connections = new Map<string, ConnectionDto>();
  readonly strokes = new Map<string, StrokeDto>();
  readonly peers = new Map<string, PresenceDto>();

  /** nodeId to the peer holding it. */
  readonly locks = new Map<string, string>();

  selfPeerId: string | null = null;

  /**
   * Your own presence, which `peers` deliberately excludes — the join result separates
   * `Self` from `Peers` so the peer count means "other people". Kept here because your own
   * colour is still needed: a node you are holding is shown in it, same as anyone else's.
   */
  self: PresenceDto | null = null;

  constructor(private readonly listener: RoomStateListener) {}

  /**
   * Wholesale replace from a snapshot. Used on first join and after a reconnect: a
   * reconnected client has missed an unknown number of events, so patching up is not
   * possible — only reloading truth is.
   */
  /**
   * Replaces the artifact — nodes, connections and locks — while leaving the party alone.
   *
   * **The difference from {@link reset} is the entire feature.** Opening a diagram in place keeps
   * everybody where they are (SPEC-161): nobody joins, nobody leaves, and voice does not break
   * (SPEC-064). Routing a swap through `reset` would clear the peer map and this client's own
   * identity and then repopulate them from whatever the caller happened to pass, which at best
   * makes every avatar in the room blink out and back, and at worst loses the peer list entirely
   * because a swap has no reason to be carrying one.
   *
   * Strokes are replaced rather than kept: ink belongs to the room (SPEC-130), so the server's
   * snapshot carries the same board back unchanged, and taking it from the snapshot keeps this
   * method a straight application of server truth rather than a merge nobody can reason about.
   */
  applyDiagramShown(snapshot: RoomSnapshotDto, locks: NodeLockDto[]): void {
    this.nodes.clear();
    this.connections.clear();
    this.strokes.clear();
    this.locks.clear();

    for (const node of snapshot.nodes) {
      this.nodes.set(node.id, node);
    }

    for (const connection of snapshot.connections) {
      this.connections.set(connection.id, connection);
    }

    for (const stroke of snapshot.strokes) {
      this.strokes.set(stroke.id, stroke);
    }

    for (const lock of locks) {
      this.locks.set(lock.nodeId, lock.heldByPeerId);
    }

    this.listener.onReset(this);
  }

  reset(
    snapshot: RoomSnapshotDto,
    peers: PresenceDto[],
    locks: NodeLockDto[],
    self: PresenceDto,
  ): void {
    this.nodes.clear();
    this.connections.clear();
    this.strokes.clear();
    this.peers.clear();
    this.locks.clear();

    this.self = self;
    this.selfPeerId = self.peerId;

    for (const node of snapshot.nodes) {
      this.nodes.set(node.id, node);
    }

    for (const connection of snapshot.connections) {
      this.connections.set(connection.id, connection);
    }

    for (const stroke of snapshot.strokes) {
      this.strokes.set(stroke.id, stroke);
    }

    for (const peer of peers) {
      this.peers.set(peer.peerId, peer);
    }

    for (const lock of locks) {
      this.locks.set(lock.nodeId, lock.heldByPeerId);
    }

    this.listener.onReset(this);
  }

  /**
   * Applies a created or updated node.
   *
   * Treated as an upsert rather than an insert on purpose. Joining subscribes to the room
   * group *before* reading the snapshot — the reverse order would silently drop anything
   * committed while the snapshot query ran — so a client can legitimately receive
   * NodeCreated for a node the snapshot already contained.
   */
  upsertNode(node: NodeDto): void {
    const existing = this.nodes.get(node.id);

    // Events can arrive out of order, and a reconnect replays state the client already
    // has. Version is what makes both cases safe to ignore.
    if (existing && existing.version > node.version) {
      return;
    }

    this.nodes.set(node.id, node);
    this.listener.onNodeUpserted(node);
  }

  removeNode(nodeId: string): void {
    if (!this.nodes.delete(nodeId)) {
      return;
    }

    this.locks.delete(nodeId);

    // The server removes edges of a deleted node and announces each one, but applying it
    // here too keeps local state consistent regardless of event order.
    for (const [id, connection] of [...this.connections]) {
      if (connection.fromNodeId === nodeId || connection.toNodeId === nodeId) {
        this.connections.delete(id);
        this.listener.onConnectionRemoved(id);
      }
    }

    this.listener.onNodeRemoved(nodeId);
  }

  upsertConnection(connection: ConnectionDto): void {
    this.connections.set(connection.id, connection);
    this.listener.onConnectionUpserted(connection);
  }

  removeConnection(connectionId: string): void {
    if (this.connections.delete(connectionId)) {
      this.listener.onConnectionRemoved(connectionId);
    }
  }

  /**
   * Applies a committed stroke — either the server's simplified return value from this
   * client's own `CommitStroke` call, or a peer's `StrokeCreated` broadcast.
   *
   * A plain upsert, unlike `upsertNode`: a stroke has no `Version` and no update path at
   * all (plan/whiteboard.md, "No Version field" — "created once, whole, and later deleted
   * whole"), so there is nothing to compare against and no stale-event case to guard.
   */
  upsertStroke(stroke: StrokeDto): void {
    this.strokes.set(stroke.id, stroke);
    this.listener.onStrokeUpserted(stroke);
  }

  /**
   * Removes a stroke. Idempotent on purpose, matching `DeleteStroke`'s own server-side
   * no-op on a stale id (plan/whiteboard.md, "Erasing"): an eraser sweep samples contact
   * every frame, so the same stroke can plausibly be removed here twice — once from this
   * client's own successful `DeleteStroke` call, and again from the `StrokeDeleted`
   * broadcast that follows it, or from a redundant call made before the first one's
   * effect was visible locally. Silently doing nothing the second time is the point, not
   * a gap — see DrawingManager's eraseStroke.
   */
  removeStroke(strokeId: string): void {
    if (!this.strokes.delete(strokeId)) {
      return;
    }

    this.listener.onStrokeRemoved(strokeId);
  }

  /** Commits the pose a grab ended at, and frees the node. */
  applyLockReleased(nodeId: string, finalTransform: TransformDto, newVersion: number): void {
    this.locks.delete(nodeId);

    const node = this.nodes.get(nodeId);

    if (node && newVersion >= node.version) {
      const committed: NodeDto = { ...node, transform: finalTransform, version: newVersion };
      this.nodes.set(nodeId, committed);
      this.listener.onNodeUpserted(committed);
    }

    this.listener.onLocksChanged();
  }

  applyLockAcquired(nodeId: string, byPeerId: string): void {
    this.locks.set(nodeId, byPeerId);
    this.listener.onLocksChanged();
  }

  addPeer(peer: PresenceDto): void {
    this.peers.set(peer.peerId, peer);
    this.listener.onPeersChanged();
  }

  removePeer(peerId: string): void {
    if (!this.peers.delete(peerId)) {
      return;
    }

    // Locks are released by the server on disconnect, but a dropped peer must not leave a
    // node looking held by somebody who is no longer in the room.
    for (const [nodeId, holder] of [...this.locks]) {
      if (holder === peerId) {
        this.locks.delete(nodeId);
      }
    }

    this.listener.onLocksChanged();
    this.listener.onPeersChanged();
  }

  /** Colour of whoever holds this node, for the "in use" affordance. Null if free. */
  holderColor(nodeId: string): string | null {
    const holder = this.locks.get(nodeId);

    if (holder === undefined) {
      return null;
    }

    // Your own grab is worth showing too, in your own colour — and yours is not in `peers`.
    if (holder === this.selfPeerId) {
      return this.self?.colorHex ?? '#FFFFFF';
    }

    return this.peers.get(holder)?.colorHex ?? '#FFFFFF';
  }

  isHeldByOther(nodeId: string): boolean {
    const holder = this.locks.get(nodeId);
    return holder !== undefined && holder !== this.selfPeerId;
  }

  connectionsTouching(nodeId: string): ConnectionDto[] {
    return [...this.connections.values()].filter(
      (connection) => connection.fromNodeId === nodeId || connection.toNodeId === nodeId,
    );
  }
}
